import React, { createContext, useContext, useState, useCallback } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const STORAGE_KEY = 'tmj_subscribed_email';

const SubscriptionContext = createContext(null);

export const SubscriptionProvider = ({ children }) => {
  const [email, setEmail] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) || null;
    } catch {
      return null;
    }
  });

  const [showPrompt, setShowPrompt] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [promptContext, setPromptContext] = useState('');

  const isSubscribed = !!email;

  const subscribe = useCallback(async (newEmail, platforms) => {
    const body = platforms ? { email: newEmail, platforms } : { email: newEmail };
    await axios.post(`${BACKEND_URL}/api/newsletter/subscribe`, body);
    localStorage.setItem(STORAGE_KEY, newEmail);
    setEmail(newEmail);
  }, []);

  const notifyOnRelease = useCallback(async (release, withEmail) => {
    const targetEmail = withEmail || email;
    if (!targetEmail) throw new Error('No email available');
    // SEC-002 — backend now returns { status: 'pending_verification' } and
    // sends a confirm email; caller reads the response.message to update
    // the toast copy so users know to check their inbox.
    const res = await axios.post(`${BACKEND_URL}/api/notify/release`, {
      email: targetEmail,
      release_id: release.id,
      title: release.title,
      release_date: release.release_date,
      platform: release.platform || null,
    });
    return res.data;
  }, [email]);

  const signOut = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    setEmail(null);
  }, []);

  const requireSubscription = useCallback((callback, context = '') => {
    if (email) {
      callback();
      return;
    }
    setPendingAction(() => callback);
    setPromptContext(context);
    setShowPrompt(true);
  }, [email]);

  const closePrompt = useCallback(() => {
    setShowPrompt(false);
    setPendingAction(null);
    setPromptContext('');
  }, []);

  const completePending = useCallback(() => {
    if (pendingAction) {
      try {
        pendingAction();
      } catch (e) {
        console.error('Pending action error:', e);
      }
    }
    setShowPrompt(false);
    setPendingAction(null);
    setPromptContext('');
  }, [pendingAction]);

  return (
    <SubscriptionContext.Provider value={{
      email,
      isSubscribed,
      subscribe,
      notifyOnRelease,
      signOut,
      requireSubscription,
      showPrompt,
      promptContext,
      closePrompt,
      completePending,
    }}>
      {children}
    </SubscriptionContext.Provider>
  );
};

export const useSubscription = () => {
  const ctx = useContext(SubscriptionContext);
  if (!ctx) throw new Error('useSubscription must be used inside SubscriptionProvider');
  return ctx;
};
